import React, { useState, useEffect, useRef } from "react";
import { getCookie } from "utils/index"; // Ensure correct import
import AOS from "aos";
import "aos/dist/aos.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useRouter } from "next/router";
import axios from "axios";
import CollegeForm from "./collegePredictorForm";
import { CiLock } from "react-icons/ci";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// import sample from '../public/sample-predictor.png'; // Use your actual image path

const faqData = [
  {
    question: "How accurate is the NEET College Predictor 2026?",
    answer: (
      <p>
        The NEET College Predictor 2026 is based on previous years' official
        counselling data, including round-wise closing ranks for All India
        Quota and State Quota. It uses trends from multiple years to provide
        realistic predictions. However, cutoffs vary every year due to factors
        like exam difficulty and seat availability, so results should be used
        as guidance rather than a guaranteed outcome.
      </p>
    ),
  },
  {
    question: "Can I use the college predictor before NEET 2026 results are declared?",
    answer: (
      <p>
        Yes, you can use the predictor even before official results are
        announced. If you have an estimated score or expected rank, enter that
        to get an approximate list of colleges and prepare early before
        counselling starts.
      </p>
    ),
  },
  {
    question: "Does the predictor include both AIQ and State Quota colleges?",
    answer: (
      <p>
        Yes, the tool covers both All India Quota (15%) and State Quota (85%)
        colleges. Based on your domicile state and selected quota, it shows
        relevant colleges where you have a chance of admission.
      </p>
    ),
  },
  {
    question: "Will it show private and deemed medical colleges as well?",
    answer: (
      <p>
        Yes, the predictor includes government, private, and deemed
        universities. It also provides options under management and NRI quotas
        where applicable so you get a complete list of possible colleges.
      </p>
    ),
  },
  {
    question: "What details are required to use the NEET College Predictor?",
    answer: (
      <p>
        To get accurate predictions, enter your NEET rank, category, state of
        domicile, and preferred quota type. These inputs help match your
        profile with previous cutoff trends and generate personalized results.
      </p>
    ),
  },
  {
    question: "How does my category affect college prediction?",
    answer: (
      <p>
        Your category (General, OBC, SC, ST, EWS, etc.) plays a significant
        role in admission chances. Each category has different cutoff ranks due
        to reservation policies, and the predictor uses category-wise data to
        provide realistic outcomes.
      </p>
    ),
  },
  {
    question: "What is a safe rank for getting an MBBS government college?",
    answer: (
      <p>
        There is no fixed safe rank because it varies by state, category, and
        quota. In general, a rank under 50,000 can provide decent chances for
        government colleges under many State Quotas, while top AIQ colleges
        usually require significantly higher ranks.
      </p>
    ),
  },
  {
    question: "How does the predictor help in choice filling during counselling?",
    answer: (
      <p>
        It helps you shortlist colleges by admission probability and build a
        balanced list of dream, safe, and backup choices. This improves your
        overall counselling strategy and seat-allotment outcomes.
      </p>
    ),
  },
  {
    question: "Is the NEET College Predictor free to use?",
    answer: (
      <p>
        Yes, the tool is free to access and designed for quick guidance.
        Depending on account plan and access status, certain advanced outputs
        may require unlock flow.
      </p>
    ),
  },
  {
    question: "Does the predictor work for all states in India?",
    answer: (
      <p>
        Yes, it includes All India Quota data and major state counselling
        authorities across India. Results are filtered according to your
        domicile state and quota selection.
      </p>
    ),
  },
  {
    question: "What should I do after getting my prediction report?",
    answer: (
      <p>
        Shortlist colleges based on admission probability, compare fees and
        location, prepare counselling documents, and create a practical choice
        filling order before official rounds begin.
      </p>
    ),
  },
];
// Replace SelectField with a native select
const SelectField = ({ placeholder, value, onChange, children }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-[#ff9245] focus:outline-none"
  >
    <option value="" disabled>
      {placeholder}
    </option>
    {children}
  </select>
);

// Add blur-row CSS inline (for quick use)
const blurRowStyle = {
  filter: "blur(4px)",
  pointerEvents: "none",
  userSelect: "none",
  background: "#f9fafb",
};

const CollegeTable = ({
  title,
  data,
  loading,
  type,
  blurRowsCount = 0,
  onLockClick,
  tableRef,
  onDownloadPdf,
  showDownloadButton,
}) => {
  const lockRowIndex = blurRowsCount > 0 ? Math.floor(blurRowsCount / 2) : -1;
  const showStateFields = type === "state";

  // Responsive state for desktop/mobile
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  return (
    <div className="w-full bg-white p-8 rounded-xl shadow-lg mb-8 mt-4 sm:mt-12 relative">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-center text-2xl font-semibold text-teal-700 mb-4">
          {title}
        </h2>
        {showDownloadButton && data && data.length > 0 && (
          <button
            onClick={onDownloadPdf}
            className="ml-auto bg-[#ff9245] text-white px-4 py-2 rounded-lg shadow hover:bg-[#ffb97a] transition-colors duration-200 text-sm font-semibold"
            style={{ position: "relative", right: 0 }}
          >
            Download as PDF
          </button>
        )}
      </div>
      <div className="overflow-x-auto relative">
        {/* Lock icon and text: show as absolute overlay for all screens */}
        {blurRowsCount > 0 && data.length > 0 && (
          <>
            <div
              className="block"
              style={{
                position: "absolute",
                left: "50%",
                // Center vertically on desktop, keep original on mobile
                top: isDesktop ? "30%" : "80px",
                transform: isDesktop
                  ? "translate(-50%, -50%)"
                  : "translateX(-50%)",
                zIndex: 10,
                cursor: "pointer",
                width: "max-content",
              }}
              onClick={onLockClick}
            >
              <CiLock
                size={60}
                color="#ff9245"
                style={{
                  background: "white",
                  borderRadius: "50%",
                }}
              />
            </div>
            <div
              className="block"
              style={{
                position: "absolute",
                left: "50%",
                // Center vertically on desktop, keep original on mobile
                top: isDesktop ? "calc(30% + 55px)" : "145px",
                transform: isDesktop
                  ? "translate(-50%, -50%)"
                  : "translateX(-50%)",
                zIndex: 10,
                fontWeight: 600,
                color: "#ff9245",
                fontSize: "1.1rem",
                background: "white",
                borderRadius: "16px",
                padding: "4px 18px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                cursor: "pointer",
                textAlign: "center",
                width: "max-content",
                maxWidth: "90vw",
              }}
              onClick={onLockClick}
            >
              Enroll with NEET Support to unlock your top college possibilities
            </div>
          </>
        )}
        <div ref={tableRef}>
          <table className="min-w-full table-auto border border-gray-300 text-left border-collapse">
            <thead className="bg-gray-100 text-[#004959] md:text-md text-md sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 border border-gray-300">College Name</th>
                <th className="px-4 py-2 border border-gray-300">State</th>
                <th className="px-4 py-2 border border-gray-300 text-center">Max Rank</th>
                {showStateFields && (
                  <th className="px-4 py-2 border border-gray-300">Alloted Category</th>
                )}
                <th className="px-4 py-2 border border-gray-300">Category</th>
                {showStateFields && (
                  <th className="px-4 py-2 border border-gray-300">Quota</th>
                )}
                <th className="px-4 py-2 border border-gray-300">Round</th>
                <th className="px-4 py-2 border border-gray-300">Type</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={showStateFields ? "8" : "6"} className="text-center py-6 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((college, index) => (
                  (() => {
                    const row = college?._doc || college;
                    const minRank = college?.MinRank ?? row?.MinRank ?? row?.Rank;
                    const maxRank = college?.MaxRank ?? row?.MaxRank ?? row?.Rank;

                    return (
                  <tr
                    key={index}
                    className="hover:bg-gray-50"
                    style={index < blurRowsCount ? blurRowStyle : undefined}
                  >
                    <td className="px-4 py-2 border border-gray-300 font-medium">
                      {row["Allotted Institute"]}
                    </td>
                    <td className="px-4 py-2 border border-gray-300">
                      {row["State"]}
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      {maxRank}
                    </td>
                    {showStateFields && (
                      <td className="px-4 py-2 border border-gray-300">
                        {row["Alloted Category"]}
                      </td>
                    )}
                    <td className="px-4 py-2 border border-gray-300">
                      {row["Candidate Category"]}
                    </td>
                    {showStateFields && (
                      <td className="px-4 py-2 border border-gray-300">
                        {row["Allotted Quota"]}
                      </td>
                    )}
                    <td className="px-4 py-2 border border-gray-300">
                      {row["Rounds"]}
                    </td>
                    <td className="px-4 py-2 border border-gray-300">
                      {row["Type of College"]}
                    </td>
                  </tr>
                    );
                  })()
                ))
              ) : (
                <tr>
                  <td
                    colSpan={showStateFields ? "8" : "6"}
                    className="text-center p-3 border border-gray-300"
                  >
                    {type === "mcc"
                      ? "No MCC Colleges Available"
                      : "No State Colleges Available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


export default function CollegePredictor({ pricingData }) {
  const [marks, setMarks] = useState("");
  const [activeFAQ, setActiveFAQ] = useState(null);

  const [mccQuota, setMccQuota] = useState("");
  const [round, setRound] = useState("1");

  const [predictedRanks, setPredictedRanks] = useState([]);
  const [neetRank, setNeetRank] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  // const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showCollegePredictorForm, setShowCollegePredictorForm] = useState(false);
  const [error, setError] = useState(null);

  const [domicileState, setDomicileState] = useState("");
  const [category, setCategory] = useState("");

  const [mccColleges, setMccColleges] = useState([]);
  const [stateColleges, setStateColleges] = useState([]);
  const [mccPage, setMccPage] = useState(1);
  const [statePage, setStatePage] = useState(1);
  const [pageLimit] = useState(10);
  const [hasPaidOneTimeUsed, setHasPaidOneTimeUsed] = useState(false);
  const [pagination, setPagination] = useState({
    mcc_page: 1,
    state_page: 1,
    limit: 10,
    mcc_total: 0,
    mcc_total_pages: 0,
    state_total: 0,
    state_total_pages: 0,
  });
  const [mccQuotas, setMccQuotas] = useState([]);
  const [loadingMCC, setLoadingMCC] = useState(false); // New loading state for MCC
  const [loadingState, setLoadingState] = useState(false); // New loading state for State colleges
  const [hasPaidAccess, setHasPaidAccess] = useState(false);
  const [userPlan, setUserPlan] = useState("guest");
  const [hasFreeTrialUsed, setHasFreeTrialUsed] = useState(true);

  const [filters, setFilters] = useState({
    subCategories: [],
    mccQuotas: [],
    categories: [],
  });
  const [stateCategory, setStateCategory] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const authToken = getCookie("authToken");
  const isLoggedIn = !!authToken;

  const API_URL = process.env.NEXT_PUBLIC_API_URL;

  // Refs for tables
  const mccTableRef = useRef(null);
  const stateTableRef = useRef(null);

  // PDF download handlers
  const handleDownloadPdf = async (ref, title) => {
    if (!ref.current) return;
    const element = ref.current;
    // Save original style
    const originalMinWidth = element.style.minWidth;
    const originalWidth = element.style.width;
    const originalMinHeight = element.style.minHeight;
    const originalHeight = element.style.height;
    // Set width to 1440px or scrollWidth (whichever is larger), and minHeight to scrollHeight
    const targetWidth = Math.max(1440, element.scrollWidth);
    element.style.minWidth = targetWidth + 'px';
    element.style.width = targetWidth + 'px';
    element.style.minHeight = element.scrollHeight + 'px';
    element.style.height = element.scrollHeight + 'px';
    // Wait for browser to reflow
    await new Promise((resolve) => setTimeout(resolve, 50));
    // Use html2canvas with adjusted width and height
    const canvas = await html2canvas(element, {
      scale: 1,
      width: targetWidth,
      height: element.scrollHeight,
      windowWidth: targetWidth,
      windowHeight: element.scrollHeight,
      scrollY: -window.scrollY,
    });
    // Restore original style
    element.style.minWidth = originalMinWidth;
    element.style.width = originalWidth;
    element.style.minHeight = originalMinHeight;
    element.style.height = originalHeight;
    const imgData = canvas.toDataURL("image/png");
    // Dynamically set PDF page size to fit image
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    // 1 px = 0.75 pt (jsPDF uses pt)
    const pdfWidth = imgWidth * 0.75;
    const pdfHeight = imgHeight * 0.75 + 60; // +60 for title
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: [pdfWidth, pdfHeight] });
    pdf.text(title, 30, 40);
    pdf.addImage(imgData, "PNG", 20, 50, pdfWidth - 40, pdfHeight - 70);
    pdf.save(`${title.replace(/\s+/g, "_")}_Table.pdf`);
  };

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 1000,
      offset: 150,
      once: true,
    });
  }, []);

  const domicileStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const router = useRouter();

  const normalizeTier = (tier) =>
    String(tier || "")
      .trim()
      .toLowerCase();

  const hasPlanAccess = ["basic", "premium"].includes(normalizeTier(userPlan));
  const hasFreeOneTimeAccess =
    normalizeTier(userPlan) === "free" && !hasFreeTrialUsed;
  const hasPredictorAccess = hasPaidAccess || hasPlanAccess || hasFreeOneTimeAccess;

  useEffect(() => {
    if (!isLoggedIn || !authToken) {
      setUserPlan("guest");
      setHasFreeTrialUsed(true);
      return;
    }

    const fetchUserPlan = async () => {
      try {
        const response = await fetch(`${API_URL}/api/verify-user`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const result = await response.json();
        if (result?.success && Array.isArray(result?.tier) && result.tier.length > 0) {
          const currentTier = normalizeTier(result.tier[result.tier.length - 1]);
          setUserPlan(currentTier || "free");
        } else {
          setUserPlan("free");
        }
      } catch (planError) {
        console.error("Error checking user tier:", planError);
        setUserPlan("free");
      }
    };

    fetchUserPlan();
  }, [isLoggedIn, authToken, API_URL]);

  useEffect(() => {
    if (!isLoggedIn || normalizeTier(userPlan) !== "free") {
      setHasFreeTrialUsed(true);
      return;
    }

    try {
      const key = "collegePredictorFreeTrialUsed";
      const value = localStorage.getItem(key) === "1";
      setHasFreeTrialUsed(value);
    } catch (storageError) {
      console.error("Error reading free predictor usage:", storageError);
      setHasFreeTrialUsed(true);
    }
  }, [isLoggedIn, userPlan]);

  useEffect(() => {
    if (!router.isReady) return;
    const { rank, predictionPaymentId } = router.query;

    if (rank) {
      setNeetRank(rank); // Set NEET rank from query parameter
    }

    if (!predictionPaymentId || !isLoggedIn || !authToken) return;

    const verifyPredictionPayment = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/college-predictor/payment-status/${predictionPaymentId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        const result = await response.json();

        if (result?.code === "PAYMENT_PENDING") {
          toast.info(result?.message || "Payment pending");
        }

        if (result?.code === "PAYMENT_FAILED") {
          toast.error(result?.message || "Payment failed");
        }

        if (result?.code === "PAYMENT_NOT_FOUND") {
          toast.error(result?.message || "Payment record not found");
        }

        if (result?.success && result?.code === "PAYMENT_SUCCESS") {
          toast.success("Payment successful. College Predictor unlocked.");
          setHasPaidAccess(true);
          setHasPaidOneTimeUsed(false);
          try {
            localStorage.removeItem("collegePredictorPaidOneTimeUsed");
          } catch (storageError) {
            console.error("Error resetting paid predictor usage:", storageError);
          }
          setShowCollegePredictorForm(false);
          let restoredFilters = null;

          try {
            const saved = localStorage.getItem("collegePredictorAfterPayFilters");
            if (saved) {
              const parsed = JSON.parse(saved);
              restoredFilters = {
                neetRank: parsed?.neetRank || "",
                domicileState: parsed?.domicileState || "",
                mccQuota: parsed?.mccQuota || "",
                category: parsed?.category || "",
                stateCategory: parsed?.stateCategory || "",
                round: parsed?.round || "1",
              };
              if (parsed?.neetRank) setNeetRank(parsed.neetRank);
              if (parsed?.domicileState) setDomicileState(parsed.domicileState);
              if (parsed?.mccQuota) setMccQuota(parsed.mccQuota);
              if (parsed?.category) setCategory(parsed.category);
              if (parsed?.stateCategory) setStateCategory(parsed.stateCategory);
              if (parsed?.round) setRound(parsed.round);
            }
          } catch (storageError) {
            console.error("Error restoring predictor filters:", storageError);
          }

          setTimeout(() => {
            fetchCollegeData(restoredFilters);
          }, 0);

        }

        const cleanQuery = { ...router.query };
        delete cleanQuery.predictionPaymentId;
        router.replace({ pathname: router.pathname, query: cleanQuery }, undefined, {
          shallow: true,
        });
      } catch (paymentError) {
        console.error("Prediction payment verification failed:", paymentError);
        toast.error("Could not verify payment. Please try again.");
      }
    };

    verifyPredictionPayment();
  }, [router.isReady, router.query, isLoggedIn, authToken, API_URL]);

  useEffect(() => {
    if (!isLoggedIn || !authToken) {
      setHasPaidAccess(false);
      setHasPaidOneTimeUsed(false);
      return;
    }

    const checkAccess = async () => {

      useEffect(() => {
        if (!isLoggedIn || !authToken) {
          setHasPaidOneTimeUsed(false);
          return;
        }

        try {
          const key = "collegePredictorPaidOneTimeUsed";
          const value = localStorage.getItem(key) === "1";
          setHasPaidOneTimeUsed(value);
        } catch (storageError) {
          console.error("Error reading paid predictor usage:", storageError);
          setHasPaidOneTimeUsed(false);
        }
      }, [isLoggedIn, authToken, hasPaidAccess]);
      try {
        const response = await fetch(`${API_URL}/api/college-predictor/access`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();
        setHasPaidAccess(!!result?.hasAccess);
      } catch (accessError) {
        console.error("Error checking predictor access:", accessError);
        setHasPaidAccess(false);
      }
    };

    checkAccess();
  }, [isLoggedIn, authToken, API_URL]);

  const fetchFilters = async (state) => {
    if (!state) return; // Ensure state is provided

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/new-getcategory`, // Backend endpoint
        { state },
        {
          headers: {
            "Content-Type": "application/json",
            ...(isLoggedIn && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      const data = response.data.filters;
      setFilters({
        subCategories: data.state_categories || [],
        mccQuotas: data.mcc_quotas || [],
        categories: data.mcc_categories || [],
      });
      setStateCategory(""); // Reset state category when state changes
    } catch (error) {
      console.error("Error fetching filters:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch filters."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCollegeData = async (overrides = null) => {
    const effectiveFilters = {
      neetRank,
      domicileState,
      mccQuota,
      category,
      stateCategory,
      round,
      mccPage,
      statePage,
      limit: pageLimit,
      ...(overrides || {}),
    };

    if (!effectiveFilters.neetRank) {
      setError("Please enter a valid NEET rank.");
      return;
    }
    setLoading(true);
    setError(null);
    setLoadingMCC(true); // Set MCC table loading
    setLoadingState(true); // Set State table loading

    try {
      const response = await axios.post(
        `${API_URL}/api/new-filter-data`, // Assuming this is the correct endpoint
        {
          rank: Number(effectiveFilters.neetRank),
          domicile: effectiveFilters.domicileState,
          mcc_quota: effectiveFilters.mccQuota,
          category: effectiveFilters.category,
          state_category: effectiveFilters.stateCategory,
          round: effectiveFilters.round,
          mcc_page: effectiveFilters.mccPage,
          state_page: effectiveFilters.statePage,
          limit: effectiveFilters.limit,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(isLoggedIn && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      const data = response.data;
      if (data.error) {
        throw new Error(data.error);
      }

      setMccColleges(data.mcc_result || []);
      setStateColleges(data.state_result || []);
      setPagination(
        data.pagination || {
          mcc_page: Number(effectiveFilters.mccPage) || 1,
          state_page: Number(effectiveFilters.statePage) || 1,
          limit: Number(effectiveFilters.limit) || pageLimit,
          mcc_total: 0,
          mcc_total_pages: 0,
          state_total: 0,
          state_total_pages: 0,
        }
      );
    } catch (error) {
      setError(error.message || "Failed to fetch data.");
    } finally {
      setLoading(false);
      setLoadingMCC(false); // Stop MCC loading
      setLoadingState(false); // Stop State loading
    }
  };

  useEffect(() => {
    if (domicileState) {
      fetchFilters(domicileState);
    }
  }, [domicileState]);

  useEffect(() => {
    if (domicileState) {
      fetchMccQuotas(domicileState);
    }
  }, [domicileState]);
  // Handle form submission
  // const handleSubmit = (e) => {
  //   e.preventDefault();filter-data
  //   showCollegePredictorForm(true);
  //   fetchCollegeData();
  // };

  const fetchMccQuotas = async (state) => {
    if (!state) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/new-get-mcc-quotas`,
        { state },
        {
          headers: {
            "Content-Type": "application/json",
            ...(isLoggedIn && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      setMccQuotas(response.data.mccQuotas || []);
    } catch (error) {
      console.error("Error fetching MCC Quotas:", error);
      setError(error.message || "Failed to fetch MCC Quotas");
    }
  };

  // Submit flow:
  // - paid/basic/premium users fetch directly.
  // - free one-time users open lead form first, then first submit unlocks results.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMccPage(1);
    setStatePage(1);

    if (hasPaidAccess && hasPaidOneTimeUsed) {
      setMccColleges([]);
      setStateColleges([]);
      setShowCollegePredictorForm(true);
      return;
    }

    if (hasPaidAccess || hasPlanAccess) {
      await fetchCollegeData({ mccPage: 1, statePage: 1 });

      if (hasPaidAccess && !hasPlanAccess) {
        try {
          localStorage.setItem("collegePredictorPaidOneTimeUsed", "1");
        } catch (storageError) {
          console.error("Error storing paid predictor usage:", storageError);
        }
        setHasPaidOneTimeUsed(true);
      }
      return;
    }

    if (hasFreeOneTimeAccess) {
      // Keep previous results hidden when lead modal opens for first-time free flow.
      setMccColleges([]);
      setStateColleges([]);
      setShowCollegePredictorForm(true);
      return;
    }

    // Safety check: if user has already paid predictor amount (Rs 49), allow direct access.
    if (isLoggedIn && authToken) {
      try {
        const accessResponse = await fetch(`${API_URL}/api/college-predictor/access`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const accessResult = await accessResponse.json();

        if (accessResult?.hasAccess) {
          setHasPaidAccess(true);
          await fetchCollegeData({ mccPage: 1, statePage: 1 });

          try {
            localStorage.setItem("collegePredictorPaidOneTimeUsed", "1");
          } catch (storageError) {
            console.error("Error storing paid predictor usage:", storageError);
          }
          setHasPaidOneTimeUsed(true);
          return;
        }
      } catch (accessError) {
        console.error("Error re-checking predictor access on submit:", accessError);
      }
    }

    // Keep previous results hidden when modal opens in locked flow.
    setMccColleges([]);
    setStateColleges([]);
    setShowCollegePredictorForm(true);
  };

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  const handleMccPageChange = (nextPage) => {
    const maxPages = Math.max(Number(pagination?.mcc_total_pages || 0), 1);
    if (nextPage < 1 || nextPage > maxPages) return;
    setMccPage(nextPage);
    fetchCollegeData({ mccPage: nextPage, statePage });
  };

  const handleStatePageChange = (nextPage) => {
    const maxPages = Math.max(Number(pagination?.state_total_pages || 0), 1);
    if (nextPage < 1 || nextPage > maxPages) return;
    setStatePage(nextPage);
    fetchCollegeData({ mccPage, statePage: nextPage });
  };

  // Blur data for users who don't have access via plan/payment/free one-time attempt.
  const isFreeOrGuest = !hasPredictorAccess;

  return (
    <>
      <ToastContainer />
      <div className="w-full flex flex-col">
        <div className="bg-[#005A53] min-h-[550px] lg:min-h-[700px] w-full flex items-center justify-between px-9 2xl:px-40 lg:pt-20">
          {/* Left Side: Form */}
          <div className="w-full lg:w-1/2 flex flex-col items-center justify-center my-16">
            <div className="w-full max-w-2xl text-white">
              <h1 className="text-2xl lg:text-4xl font-bold mb-4 text-center pt-16 lg:pt-0">
                NEET College  <span className="text-[#ff9245]">Predictor 2026</span>
              </h1>
              <p className="text-md lg:text-lg mb-8 font-medium text-center pb-4">
                Get your estimated NEET 2026 college instantly. Just input your
                rank and state, and we’ll show you the colleges you can get into
              </p>
            </div>
            <form
              className="flex flex-col items-center w-full"
              onSubmit={(e) => {
                // setInteracted(true); // Mark interaction on submit
                handleSubmit(e);
              }}
            >
              <div className="w-full max-w-2xl bg-white p-8 rounded-lg shadow-lg mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    type="number"
                    placeholder="NEET Rank"
                    value={neetRank}
                    className="py-1 px-4 w-full border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-[#ff9245] focus:outline-none"
                    onChange={(e) => {
                      // setInteracted(true); // Mark interaction
                      setNeetRank(e.target.value);
                    }}
                  />
                  <SelectField
                    placeholder="Select Domicile State"
                    value={domicileState || ""}
                    onChange={(value) => {
                      // setInteracted(true); // Mark interaction
                      setDomicileState(value);
                    }}
                  >
                    {domicileStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
                  </SelectField>
                  <SelectField
                    placeholder="Select MCC Quota"
                    value={mccQuota || ""}
                    onChange={(value) => {
                      // setInteracted(true); // Mark interaction
                      setMccQuota(value);
                    }}
                  >
                    {mccQuotas.length > 0 ? (
                      mccQuotas.map((quota, index) => (
                        <option key={index} value={quota}>
                          {quota}
                        </option>
                      ))
                    ) : (
                      <option disabled value="">
                        No MCC Quotas Available
                      </option>
                    )}
                  </SelectField>
                  <SelectField
                    placeholder="Select MCC Category"
                    value={category || ""}
                    onChange={(value) => {
                      // setInteracted(true); // Mark interaction
                      setCategory(value);
                    }}
                  >
                    {filters.categories.map((category, index) => (
                      <option key={index} value={category}>
                        {category}
                      </option>
                    ))}
                  </SelectField>
                  <SelectField
                    placeholder="Select State Category"
                    value={stateCategory || ""}
                    onChange={(value) => {
                      // setInteracted(true); // Mark interaction
                      setStateCategory(value);
                    }}
                  >
                    {filters.subCategories.map((category, index) => (
                      <option key={index} value={category}>
                        {category}
                      </option>
                    ))}
                  </SelectField>
                  <SelectField
                    placeholder="Select Round"
                    value={round || ""}
                    onChange={(value) => {
                      // setInteracted(true); // Mark interaction
                      setRound(value);
                    }}
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                  </SelectField>
                </div>
                <div className="flex justify-center mt-[17px]">
                  <button
                    type="submit"
                    className="bg-[#ff9245] text-white hover:ring-1 hover:ring-[#ff9245] hover:bg-[#fff] hover:!text-[#ff9245] duration-300 ease-in-out px-7 py-2 rounded-3xl focus:outline-none"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>

            {showCollegePredictorForm && (
              <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
                <div className="relative">
                  <button
                    className="absolute top-0 sm:top-2 right-2 sm:right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
                    onClick={() => setShowCollegePredictorForm(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                  <CollegeForm
                    tag="College Predictor"
                    neetRank={neetRank}
                    domicileState={domicileState}
                    mccQuota={mccQuota}
                    category={category}
                    stateCategory={stateCategory}
                    round={round}
                    isLoggedIn={isLoggedIn}
                    authToken={authToken}
                    predictorAmount={49}
                    onFirstAccessUnlock={async () => {
                      if (hasFreeOneTimeAccess) {
                        try {
                          localStorage.setItem("collegePredictorFreeTrialUsed", "1");
                          setHasFreeTrialUsed(true);
                        } catch (storageError) {
                          console.error("Error storing free predictor usage:", storageError);
                        }
                      }
                      if (hasPaidAccess && !hasPlanAccess) {
                        try {
                          localStorage.setItem("collegePredictorPaidOneTimeUsed", "1");
                        } catch (storageError) {
                          console.error("Error storing paid predictor usage:", storageError);
                        }
                        setHasPaidOneTimeUsed(true);
                      }
                      setShowCollegePredictorForm(false);
                      setMccPage(1);
                      setStatePage(1);
                      await fetchCollegeData({ mccPage: 1, statePage: 1 });
                    }}
                  />
                </div>
              </div>
            )}

            {error && <div className="text-center text-red-500">{error}</div>}

            {successMessage && (
              <div className="text-center text-green-500">{successMessage}</div>
            )}
          </div>
          {/* Right Side: Image */}
          <div className="w-full lg:w-1/2 hidden lg:flex justify-center items-center mt-10 md:mt-0">
            <img
              src="assets/img/home/CollegePredict/NEETPredicto.webp"
              alt="College Predictor"
              className="h-auto w-[400px] lg:w-[500px] xl:w-[600px]"
            />
          </div>
        </div>
        {/* <div className="mx-6 my-20 md:mx-16 xl:mx-40 2xl:mx-60 bg-white shadow-md rounded-xl p-6 sm:p-10 border border-gray-200"> */}
        {error && <div className="text-center text-red-500">{error}</div>}

        {successMessage && (
          <div className="text-center text-green-500">{successMessage}</div>
        )}

        {/* College Tables */}
        <div className="bg-gray-100 px-2 lg:px-12">
          <CollegeTable
            title="MCC Colleges"
            data={mccColleges}
            loading={loadingMCC}
            type="mcc"
            blurRowsCount={isFreeOrGuest ? 5 : 0}
            onLockClick={() => setShowCollegePredictorForm(true)}
            tableRef={mccTableRef}
            onDownloadPdf={() => handleDownloadPdf(mccTableRef, "MCC Colleges")}
            showDownloadButton={!isFreeOrGuest}
          />

          <div className="w-full bg-white p-4 rounded-xl shadow-sm mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              MCC Results: {pagination?.mcc_total || 0}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleMccPageChange((pagination?.mcc_page || mccPage) - 1)}
                disabled={(pagination?.mcc_page || mccPage) <= 1 || loading}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {pagination?.mcc_page || mccPage} of {Math.max(pagination?.mcc_total_pages || 0, 1)}
              </span>
              <button
                type="button"
                onClick={() => handleMccPageChange((pagination?.mcc_page || mccPage) + 1)}
                disabled={(pagination?.mcc_page || mccPage) >= Math.max(pagination?.mcc_total_pages || 0, 1) || loading}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <CollegeTable
            title="State Colleges"
            data={stateColleges}
            loading={loadingState}
            type="state"
            blurRowsCount={isFreeOrGuest ? 5 : 0}
            onLockClick={() => setShowCollegePredictorForm(true)}
            tableRef={stateTableRef}
            onDownloadPdf={() => handleDownloadPdf(stateTableRef, "State Colleges")}
            showDownloadButton={!isFreeOrGuest}
          />

          <div className="w-full bg-white p-4 rounded-xl shadow-sm mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              State Results: {pagination?.state_total || 0}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleStatePageChange((pagination?.state_page || statePage) - 1)}
                disabled={(pagination?.state_page || statePage) <= 1 || loading}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {pagination?.state_page || statePage} of {Math.max(pagination?.state_total_pages || 0, 1)}
              </span>
              <button
                type="button"
                onClick={() => handleStatePageChange((pagination?.state_page || statePage) + 1)}
                disabled={(pagination?.state_page || statePage) >= Math.max(pagination?.state_total_pages || 0, 1) || loading}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="mx-6 my-20 md:mx-16 xl:mx-40 2xl:mx-60 bg-white shadow-md rounded-xl p-6 sm:p-10 border border-gray-200">
          <section>
            <h2 className="text-2xl sm:text-4xl font-bold text-center mt-6 mb-10">
              Introduction - Plan Your{" "}
              <span className="text-[#ff9245]">NEET Counselling</span> with
              Clarity
            </h2>
            <p className="text-gray-700 text-base sm:text-md leading-relaxed">
              The NEET UG examination is one of the most competitive entrance
              exams in India for students aspiring to pursue medicine. Every
              year, lakhs of candidates appear for NEET to secure admission into
              MBBS, BDS, and related medical courses across government, private,
              and deemed universities.
            </p>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              Clearing the exam is only the first milestone. The next and equally
              important phase is counselling and college selection, where your
              rank, category, quota, and state eligibility shape your final
              admission chances.
            </p>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              At this stage, many students face confusion because of multiple
              counselling authorities, AIQ vs State Quota differences,
              category-based reservations, changing cutoffs, and multiple rounds
              with fluctuating ranks.
            </p>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              NEET Support College Predictor 2026 is designed to solve this with
              a data-driven approach. It predicts likely MBBS and BDS options
              using your NEET rank, category, domicile state, quota preference,
              and other profile inputs against verified historical counselling
              data from MCC and state authorities.
            </p>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              Instead of relying on assumptions, you can shortlist realistic
              colleges, estimate admission probability, reduce counselling
              confusion, and plan your strategy with confidence.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              How to Use the NEET College Predictor 2026?
            </h3>
            <p className="text-gray-700">
              Using the NEET College Predictor is simple, but each input plays a
              critical role in accuracy. The tool analyzes your profile and
              matches it with historical counselling trends to produce
              personalized predictions.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mt-2">
              <li>
                <strong>NEET UG 2026 Rank:</strong> Enter your official NEET UG
                2026 rank. This is the primary signal used to compare your
                profile against previous round-wise closing trends.
              </li>
              <li>
                <strong>
                  Your Category (General, OBC, SC, ST, EWS, etc.):
                </strong>{" "}
                Category-specific reservation cutoffs vary significantly. This
                input ensures realistic category-wise prediction.
              </li>
              <li>
                <strong>State of Domicile:</strong> Your home state determines
                eligibility for State Quota seats, which form a major share of
                government seat allocation.
              </li>
              <li>
                <strong>Gender:</strong> Certain institutions and seat matrices
                may be influenced by gender distribution policies.
              </li>
              <li>
                <strong>
                  Quota Type (All India Quota, State Quota, Deemed Universities,
                  Private, NRI, etc.):
                </strong>{" "}
                Quota selection determines where you are eligible and filters
                colleges based on your target admission pathway.
              </li>
            </ul>
            <p className="text-gray-700 mt-2">
              After entering these details, the system processes your profile
              against multi-round historical data and generates a custom college
              list aligned with your realistic admission range.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              Understanding NEET Counselling Before Using the Predictor
            </h3>
            <p className="text-gray-700">
              Before using any prediction tool, understanding counselling
              pathways helps you interpret results correctly.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mt-2">
              <li>
                <strong>All India Quota (AIQ - 15%):</strong> Open to candidates
                from all states, conducted nationally, includes top government
                colleges, and has high competition.
              </li>
              <li>
                <strong>State Quota (85%):</strong> Primarily for domicile
                candidates, conducted by state authorities, and often offers
                different cutoff patterns than AIQ.
              </li>
              <li>
                <strong>Deemed and Private Universities:</strong> Wider seat
                availability in many cases, different fee structures, and
                category/quota-based variation across rounds.
              </li>
            </ul>
            <p className="text-gray-700 mt-2">
              Understanding these routes helps you choose the right filters and
              use predictions strategically.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              What You’ll Get in the Prediction Report
            </h3>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              After submitting your details, you receive a structured report
              designed for fast and practical decision-making.
            </p>
            <p className="text-gray-700 mt-2 font-semibold">Categorized college list:</p>
            <ul className="list-disc list-inside text-gray-700 ml-2">
                <li>All India Quota (AIQ)</li>
                <li>State Quota (based on your domicile)</li>
                <li>Deemed Universities</li>
                <li>Private Medical Colleges</li>
                <li>NRI/Management Quota (if applicable)</li>
            </ul>
            <p className="text-gray-700 mt-3 font-semibold">Detailed college data:</p>
            <ul className="list-disc list-inside text-gray-700 ml-2">
                <li>College name and type (Government, Private, Deemed)</li>
                <li>Course offered (MBBS/BDS)</li>
                <li>State in which the college is located</li>
                <li>Previous year closing rank (round-wise)</li>
                <li>Admission probability (High, Medium, Low)</li>
                <li>Shortlisting support for choice filling strategy</li>
            </ul>
            <p className="text-gray-700 mt-2">
              This structure helps you compare options quickly, identify
              realistic targets, and prioritize colleges effectively.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              NEET Rank vs College - What to Expect
            </h3>
            <p className="text-gray-700">
              Exact outcomes vary by year, but broad rank trends help set
              practical expectations.
            </p>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full border border-gray-300 text-left border-collapse text-gray-700">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 border border-gray-300">NEET Rank Range</th>
                    <th className="px-4 py-2 border border-gray-300">Expected College Options</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border border-gray-300">Under 5,000</td>
                    <td className="px-4 py-2 border border-gray-300">Top government medical colleges</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-300">5,000 - 15,000</td>
                    <td className="px-4 py-2 border border-gray-300">Strong government options via AIQ and State Quota</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-300">15,000 - 50,000</td>
                    <td className="px-4 py-2 border border-gray-300">Mid-tier government and private colleges</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-300">50,000 - 1,00,000</td>
                    <td className="px-4 py-2 border border-gray-300">Private medical colleges and selected BDS options</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-300">Above 1,00,000</td>
                    <td className="px-4 py-2 border border-gray-300">Private, deemed, management, and BDS pathways</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-700 mt-2">
              The predictor refines this baseline further using your category,
              domicile, and quota selections.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              How the Predictor Helps in Choice Filling Strategy
            </h3>
            <p className="text-gray-700">
              One major benefit of this tool is strategy-building for official
              choice filling. You can segment your list into:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mt-2">
              <li>
                <strong>Dream colleges:</strong> Slightly above your current
                range, competitive but possible.
              </li>
              <li>
                <strong>Safe colleges:</strong> Within your likely admission
                window with stronger probability.
              </li>
              <li>
                <strong>Backup colleges:</strong> Lower cutoff ranges to protect
                seat security.
              </li>
            </ul>
            <p className="text-gray-700 mt-2">
              This balanced approach improves seat-allotment outcomes and reduces
              last-minute decision pressure.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-semibold mb-4 mt-8">
              Why Use NEET Support's College Predictor?
            </h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-3">
              <li>
                <strong>Backed by real counselling data:</strong>
                <br />
                Verified multi-year round-wise trends across AIQ and State
                counselling systems improve reliability.
              </li>
              <li>
                <strong>Personalized predictions:</strong>
                <br />
                Outcomes are tailored using rank, category, domicile, quota, and
                profile-based eligibility filters.
              </li>
              <li>
                <strong>Saves time and effort:</strong>
                <br />
                You avoid manual comparison of scattered cutoff documents and get
                results in one place.
              </li>
              <li>
                <strong>Supports early planning:</strong>
                <br />
                You can pre-plan documents, shortlist preferences, and prepare a
                counselling strategy before official rounds.
              </li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              Who Should Use This Tool?
            </h3>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              The NEET Support College Predictor is designed to help a wide
              range of users. Here’s how different groups can benefit from using
              the tool:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-3">
              <li>
                <strong>
                  Students with NEET UG 2026 results:
                </strong>
                If you already have your rank, the predictor gives a clear,
                realistic view of admission possibilities.
              </li>
              <li>
                <strong>
                  Students waiting for results:
                </strong>
                You can use expected rank ranges to estimate colleges and prepare
                in advance.
              </li>
              <li>
                <strong>
                  Parents and guardians:
                </strong>
                The report helps families evaluate options, compare institutions,
                and support informed counselling decisions.
              </li>
              <li>
                <strong>
                  Students confused between AIQ and State Quota:
                </strong>
                The predictor allows direct comparison and highlights where your
                profile has stronger admission probability.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              Important Note on Accuracy
            </h3>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              While the predictor is highly data-driven, these realities should
              be considered:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>
                <strong>Year-to-year variation:</strong> Exam difficulty,
                participation volume, and seat dynamics shift cutoff patterns.
              </li>
              <li>
                <strong>Policy and seat updates matter:</strong> Reservation,
                eligibility, and seat changes can alter final allotment outcomes.
              </li>
              <li>
                <strong>
                  Use the predictor as strategic guidance, not guaranteed result:
                </strong>{" "}
                Final admission depends on official seat allotment by counselling
                authorities.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-semibold mb-4 mt-8">
              Benefits of Using the Predictor Early
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Better preparation and counselling planning</li>
              <li>Reduced stress during live rounds</li>
              <li>More time to research colleges and fee structures</li>
              <li>Improved decision quality during choice filling</li>
            </ul>
            <p className="text-gray-700 mt-2">
              Early planning gives you a meaningful competitive advantage in the
              counselling cycle.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-semibold mb-4 mt-8">
              Get Started - Predict Your NEET 2026 College
            </h3>
            <p className="text-gray-700 text-base sm:text-md mt-4 leading-relaxed">
              If you are unsure about your college options based on NEET UG 2026
              rank, now is the right time to act.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Enter your details in the predictor</li>
              <li>View colleges by category, domicile, and quota</li>
              <li>Shortlist options and prepare your counselling order</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Start using the NEET Support College Predictor and take control of
              your medical admission journey with clarity and confidence.
            </p>
          </section>

          <section>
            <h3 className="text-2xl font-semibold mb-4 mt-8">
              Related Tools & Resources You May Like
            </h3>
            <ul className="list-disc list-inside text-[#097F78] font-medium space-y-1">
              <li>
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() =>
                    (window.location.href =
                      "https://neetsupport.com/neet-rank-predictor")
                  }
                >
                  NEET UG Rank Predictor 2026
                </span>
              </li>
              <li>
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() =>
                    (window.location.href =
                      "https://neetsupport.com/counselling")
                  }
                >
                  NEET UG 2026 Counselling Guide
                </span>
              </li>
              <li>
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() =>
                    (window.location.href =
                      "https://neetsupport.com/blogs/expected-cut-off-and-safe-score-for-mbbs-and-bds-admission")
                  }
                >
                  Expected NEET Cutoff & Safe Scores
                </span>
              </li>
              <li>
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() =>
                    (window.location.href =
                      "https://neetsupport.com/blogs/neet-ug-result")
                  }
                >
                  NEET UG 2026 Results & Analysis
                </span>
              </li>
            </ul>
          </section>
        </div>
        <section className="w-full max-w-7xl mx-auto my-16 sm:mb-0">
          <h2
            className="sm:text-3xl text-2xl font-bold text-center capitalize"
            style={{ color: "#140342", fontSize: "28.125px" }}
          >
            Frequently asked <span className="text-[#ff9245]">questions</span>
          </h2>
          <div className="mt-8 space-y-4 ml-6 mr-6">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg shadow-md ${
                  activeFAQ === index ? "bg-gray-50" : ""
                }`}
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex justify-between items-center cursor-pointer">
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: "#140342", fontSize: "16.875px" }}
                  >
                    {faq.question}
                  </h3>
                  <span>{activeFAQ === index ? "-" : "+"}</span>
                </div>
                <div
                  className={`mt-2 text-gray-600 text-left text-lg ${
                    activeFAQ === index ? "block" : "hidden"
                  }`}
                >
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
